import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, CreditCard, Zap } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../utils/designSystem'
import SubscriptionDebugModal from './SubscriptionDebugModal'

/**
 * Overlay fullscreen yang menampilkan pesan subscription error
 * dengan background gelap dan tombol aksi
 */
const NoSubscriptionOverlay = ({
  reason = 'unknown',
  message = 'Terjadi kesalahan saat memeriksa akses',
  needsUpgrade = false,
  packageName = null,
  creditsRemaining = null,
  creditsRequired = null,
  isFreeUser = false,
  onRetry
}) => {
  const navigate = useNavigate()
  const [showDebugModal, setShowDebugModal] = useState(false)
  const isDevelopment = import.meta.env.DEV

  const getIcon = () => {
    switch (reason) {
      case 'insufficient_credits':
        return <CreditCard className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
      case 'agent_not_allowed':
        return <Zap className="w-16 h-16 text-orange-500 mx-auto mb-4" />
      default:
        return <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
    }
  }

  const getTitle = () => {
    switch (reason) {
      case 'insufficient_credits':
        return 'Kredit Tidak Mencukupi'
      case 'agent_not_allowed':
        return 'Agent Tidak Tersedia'
      case 'no_subscription':
        return 'Tidak Ada Langganan Aktif'
      default:
        return 'Akses Ditolak'
    }
  }

  const getActionButton = () => {
    if (reason === 'insufficient_credits' || needsUpgrade) {
      return (
        <button
          onClick={() => navigate('/subscription')}
          className={cn(
            buttonVariants.primary,
            'px-6 py-3 text-lg font-semibold'
          )}
        >
          <CreditCard className="w-5 h-5 mr-2" />
          {isFreeUser ? 'Upgrade Sekarang' : 'Beli Kredit'}
        </button>
      )
    }

    return (
      <button
        onClick={onRetry}
        className={cn(
          buttonVariants.primary,
          'px-6 py-3 text-lg font-semibold'
        )}
      >
        Coba Lagi
      </button>
    )
  }

  return (
    <>
      {/* Overlay Background */}
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-surface-primary rounded-lg p-8 max-w-md w-full mx-4 text-center">
          {getIcon()}
          
          <h2 className="text-2xl font-bold text-text-primary mb-4">
            {getTitle()}
          </h2>
          
          <p className="text-text-secondary mb-6 leading-relaxed">
            {message}
          </p>

          {/* Credit Info */}
          {reason === 'insufficient_credits' && (
            <div className="bg-surface-secondary rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center text-sm">
                <span className="text-text-tertiary">Kredit Tersisa:</span>
                <span className="font-semibold text-text-primary">
                  {creditsRemaining?.toLocaleString('id-ID') || 0}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm mt-2">
                <span className="text-text-tertiary">Kredit Dibutuhkan:</span>
                <span className="font-semibold text-red-500">
                  {creditsRequired?.toLocaleString('id-ID') || 0}
                </span>
              </div>
            </div>
          )}

          {/* Package Info */}
          {packageName && (
            <div className="text-sm text-text-tertiary mb-6">
              Paket Saat Ini: <span className="font-semibold">{packageName}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {getActionButton()}
            
            {/* Debug Button (Development Only) */}
            {isDevelopment && (
              <button
                onClick={() => setShowDebugModal(true)}
                className={cn(
                  buttonVariants.secondary,
                  'w-full text-sm'
                )}
              >
                Debug Info
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Debug Modal */}
      {showDebugModal && (
        <SubscriptionDebugModal
          isOpen={showDebugModal}
          onClose={() => setShowDebugModal(false)}
        />
      )}
    </>
  )
}

NoSubscriptionOverlay.propTypes = {
  reason: PropTypes.string,
  message: PropTypes.string,
  needsUpgrade: PropTypes.bool,
  packageName: PropTypes.string,
  creditsRemaining: PropTypes.number,
  creditsRequired: PropTypes.number,
  isFreeUser: PropTypes.bool,
  onRetry: PropTypes.func.isRequired
}



export default NoSubscriptionOverlay